import {
  CacheContext,
  CacheDataSet,
  CacheDataSetInfo,
  CacheExtentMode,
  CacheProgress,
  CacheStorageAdapter,
  CacheStorageDataSet,
} from '@barthauer/abc/core/data';
import { Observable, Subject } from 'rxjs';
import { MapLoader } from './map.loader';
import { MapLayerType } from './models/map.model';

declare let ol: any;
declare let proj4: any;

export class MapTileCache {
  private static readonly cacheDataSetName = 'OpenLayersTiles';
  private cacheDataSet: CacheDataSet;

  constructor(
    private mapLoader: MapLoader,
    private cacheStorage: CacheStorageAdapter,
    private olmap: any,
    private olprojection: any,
    private initialExtent: number[],
    private sources: Map<string, any>
  ) {}

  private getExtentFromContext(ctx: CacheContext) {
    const extentOption = ctx.extentMode;
    if (extentOption === CacheExtentMode.CurrentView) return this.olmap.getView().calculateExtent();
    else if (extentOption === CacheExtentMode.InitialView) return this.initialExtent;
    else if (extentOption === CacheExtentMode.UserExtent) return ctx.selectedExtent;
  }

  private downloadTilesToCache(ctx: CacheContext, item: CacheDataSet): Observable<CacheProgress> {
    const maxZoomLevel = ctx.zoomLevel;
    const curExtent = this.getExtentFromContext(ctx);
    const progress = new Subject<CacheProgress>();

    ctx.storage
      .createStorageDataSet(item, {
        extent: curExtent,
        maxZoomLevel: maxZoomLevel,
      })
      .then(async (_) => {
        const tiles = new Map<[string, number, any], any[]>();
        let tileCount = 0;
        for (let e of Array.from(this.sources)) {
          const tableName = e[0];
          const olsource = e[1];
          if (!this.isDataSourceLayerVisible(olsource)) {
            continue;
          }
          const sourceProjection = olsource.getProjection();
          const extent = ol.proj.transformExtent(curExtent, this.olprojection, sourceProjection);
          let zoom = maxZoomLevel;
          const tileGrid = olsource.getTileGrid();
          const maxZoomFromSource = this.getMaxZoomFromSource(olsource);
          if (maxZoomFromSource && zoom > maxZoomFromSource) {
            zoom = maxZoomFromSource;
          } else {
            this.updateMaxZoomForSource(olsource, maxZoomLevel, false);
          }
          const zoomArray = Array.from(Array(zoom + 1).keys());
          for (let zoom of zoomArray) {
            const tileArr = [];
            tileGrid.forEachTileCoord(extent, zoom, (tileCoord) => {
              tileArr.push(tileCoord);
              tileCount++;
            });
            tiles.set([tableName, zoom, olsource], tileArr);
          }
        }
        if (tiles.size == 0) {
          progress.next(new CacheProgress(0.5));
          return;
        }

        let stepCount = 0;
        let errorCount = 0;
        for (let e of Array.from(tiles)) {
          if (ctx.checkCancelled(progress)) {
            return;
          }
          const tableName = e[0][0];
          const zoom = e[0][1];
          const olsource = e[0][2];
          const tilesName: string = olsource.layerdefinition?.webApiTilesName;
          const dataSource: string = olsource.layerdefinition?.dataSource;
          const isVectorTile: boolean = olsource.layerdefinition?.type === MapLayerType[MapLayerType.XYZ] && !olsource.layerdefinition?.url;
          const tileArr = e[1];
          let tileDataArr: { tileKey: string; tile: any }[] = [];
          for (let i = 0; i < tileArr.length; i++) {
            if (ctx.checkCancelled(progress)) {
              return;
            }
            const tileCoord = tileArr[i];
            const tileKey = this.getTileCoordKey(tileCoord);
            let data: any = null;
            if (isVectorTile) {
              try {
                const src: string = olsource.getTileUrlFunction()(tileCoord);
                data = await this.mapLoader.loadVectorTile(tilesName, dataSource, src, tileCoord).toPromise();
                progress.next(new CacheProgress(++stepCount / tileCount));
              } catch (ex) {
                progress.next(
                  new CacheProgress(
                    stepCount / tileCount,
                    ++errorCount / tileCount,
                    this.cacheStorage.error.getFullErrorMessage(ex)
                  )
                );
              }
            } else {
              try {
                const src: string = olsource.getTileUrlFunction()(tileCoord);
                data = await this.loadImageTile(src, olsource.getTileGrid().getTileSize(zoom)).toPromise();
                progress.next(new CacheProgress(++stepCount / tileCount));
              } catch (ex) {
                progress.next(
                  new CacheProgress(
                    stepCount / tileCount,
                    ++errorCount / tileCount,
                    this.cacheStorage.error.getFullErrorMessage(ex)
                  )
                );
              }
            }
            if (data) {
              tileDataArr.push({ tileKey: tileKey, tile: data });
              if (tileDataArr.length >= 1000) {
                const copy: { tileKey: string; tile: any }[] = [];
                while (true) {
                  const x = tileDataArr.pop();
                  if (x) {
                    copy.push(x);
                  } else {
                    break;
                  }
                }
                await this.saveToCache(_, copy, tableName);
              }
            }
          }
          await this.saveToCache(_, tileDataArr, tableName);
        }
        for (let e of Array.from(this.sources)) {
          const olsource = e[1];
          if (!this.isDataSourceLayerVisible(olsource)) {
            continue;
          }
          try {
            olsource.refresh();
          } catch {}
        }
      })
      .then(() => {
        /* important ".then()" because of ".then(async ..." before! */
        progress.next(new CacheProgress(1.0));
      });

    return CacheProgress.toObservable(progress);
  }

  private getMaxZoomFromSource(source: any): number {
    const tileGrid = source.getTileGrid();
    let maxZoomFromSource = tileGrid?.maxZoomOrig;
    if (!maxZoomFromSource && tileGrid?.getMaxZoom && tileGrid?.getMaxZoom() > 0) {
      maxZoomFromSource = tileGrid.getMaxZoom();
    }
    return maxZoomFromSource;
  }

  private updateMaxZoomForSource(source: any, zoom: number, refresh: boolean): void {
    const tg = source.getTileGrid();
    if (tg?.maxZoom > zoom) {
      if (!tg.maxZoomOrig) {
        tg.maxZoomOrig = tg.maxZoom;
      }
      tg.maxZoom = zoom;
      if (refresh) {
        source.refresh();
      }
    }
  }

  private isDataSourceLayerVisible(olsource: any): boolean {
    for (let ly of this.mapLoader.getAllLayers()) {
      if (ly.layerdefinition == olsource.layerdefinition && ly.getVisible()) {
        let current: any = ly;
        do {
          let parent = this.findGroupOfLayer(this.olmap.getLayerGroup(), current);
          if (parent && !parent.getVisible()) {
            return false;
          }
          current = parent;
        } while (current);

        return true;
      }
    }
    return false;
  }

  private findGroupOfLayer(group: any, layer: any): any {
    let result;
    const layers = group.getLayers().getArray();
    for (const ly of layers) {
      if (ly === layer) {
        result = group;
      } else if (ly instanceof ol.layer.Group) {
        result = this.findGroupOfLayer(ly, layer);
      }
      if (result) {
        break;
      }
    }
    return result;
  }

  private loadImageTile(src: string, size: number): Observable<string> {
    const subject = new Subject<string>();

    const img = document.createElement('img');
    img.onload = async () => {
      let canvas;
      try {
        canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);

        subject.next(canvas.toDataURL());
        subject.complete();
      } catch (e) {
        subject.error(e);
      } finally {
        img.remove();
        canvas.remove();
      }
    };
    img.onerror = (e) => {
      let msg: any;
      const ev = e as Event;
      if (ev) {
        msg = ev.type;
      } else {
        msg = e.toString();
      }
      subject.error(msg);
      img.remove();
    };
    img.crossOrigin = 'anonymous';
    img.src = src;

    return subject.asObservable();
  }

  private getTileCoordKey(tileCoord: number[]): string {
    return tileCoord[0] + '_' + tileCoord[1] + '_' + tileCoord[2];
  }

  private async saveToCache(
    item: CacheStorageDataSet,
    tiles: {
      tileKey: string;
      tile: any;
    }[],
    tableName: string
  ): Promise<void> {
    const data = tiles.map((_) => {
      return { key: _.tileKey, value: _.tile };
    });
    await item.setData(data, tableName);
  }

  public createMapCacheItem(): CacheDataSet {
    const dataSet: CacheDataSet = new CacheDataSet(MapTileCache.cacheDataSetName);

    dataSet.tableNames = Array.from(this.sources.keys());
    dataSet.cacheMethod = (ctx) => this.downloadTilesToCache(ctx, dataSet);

    dataSet.getInfo = () => {
      let zoom = 0;
      for (let e of Array.from(this.sources)) {
        const olsource = e[1];
        if (!this.isDataSourceLayerVisible(olsource)) {
          continue;
        }
        const maxZoomFromSource = this.getMaxZoomFromSource(olsource);
        if (maxZoomFromSource > zoom) {
          zoom = maxZoomFromSource;
        }
      }
      if (zoom == 0) {
        return null;
      }
      return new CacheDataSetInfo(zoom);
    };

    this.cacheDataSet = dataSet;

    return dataSet;
  }

  public initialize(): Promise<void> {
    return this.cacheStorage
      .getStorageDataSet(this.cacheDataSet)
      .then((_) => _?.loadMetaData())
      .then((_) => {
        const zoom = _?.maxZoomLevel;
        if (zoom) {
          const arr = Array.from(this.sources.values());
          for (const l of this.mapLoader.getAllLayers()) {
            if (l.getSource) {
              const src = l.getSource();
              if (src && src.getTileGrid && arr.indexOf(src) >= 0) {
                this.updateMaxZoomForSource(src, zoom, false);
              }
            }
          }
        }
      });
  }

  public async tryLoadTile(tableName: string, tileCoord: number[]): Promise<any> {
    const storage = await this.cacheStorage.getStorageDataSet(this.cacheDataSet);
    return await storage?.get(this.getTileCoordKey(tileCoord), tableName);
  }
}
